import { useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { useShipToApi } from "./useShipToApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { assertObjectPayload } from "@utils/apiPayload";
import { isVendorId } from "@customer/Search/useSearchFilters";
import type {
    VendorResponse,
    VendorPageResponse,
    CreateVendorRequest,
    CreateVendorResponse,
    UpdateVendorRequest,
    VendorSku,
} from "@models/vendor";
import {
    vendorsAtom,
    isFetchingVendorsAtom,
    isProcessingVendorsAtom,
} from "@/store/vendorAtoms";

const VENDORS_ENDPOINT = "/vendors";

export const useVendorApi = () => {
    const { callApi } = useApi();
    const { callShipToApi } = useShipToApi();
    // Session-bound: an answer landing after sign-out must not refill it.
    const vendors = useAtomValue(vendorsAtom);
    const setVendors = useSessionSetAtom(vendorsAtom);
    const setIsFetching = useSetAtom(isFetchingVendorsAtom);
    const setIsProcessing = useSetAtom(isProcessingVendorsAtom);

    // `skipErrorNotification` is opt-in: for a caller that renders this read's
    // failure inline (the product form's vendor field). Left out, callApi's
    // own default applies and the toast stays.
    const getVendors = useCallback(
        async (
            options: { skipErrorNotification?: boolean } = {},
        ): Promise<VendorResponse[]> => {
            setIsFetching(true);
            try {
                const data = await callApi<VendorResponse[]>(VENDORS_ENDPOINT, {
                    skipErrorNotification: options.skipErrorNotification,
                });
                setVendors(data);
                return data;
            } finally {
                setIsFetching(false);
            }
        },
        [callApi, setIsFetching, setVendors],
    );

    const getVendor = useCallback(
        async (id: number): Promise<VendorResponse> => {
            return await callApi<VendorResponse>(`${VENDORS_ENDPOINT}/${id}`);
        },
        [callApi],
    );

    // The vendor page's read (#425). Not ship-to scoped: a vendor is not a
    // product. The page says "not found" itself, so only a 404 skips useApi's
    // toast; every other failure keeps it beside the page's Retry.
    const getVendorBySlug = useCallback(
        async (slug: string): Promise<VendorPageResponse> => {
            const endpoint = `${VENDORS_ENDPOINT}/slugs`;
            const vendor = await callApi<VendorPageResponse>(
                `${endpoint}/${encodeURIComponent(slug)}`,
                { skipErrorNotification: (status) => status === 404 },
            );
            assertObjectPayload(endpoint, vendor);
            // The page renders the name as its heading and filters the grid
            // by the id, so a body without both is a failed read (the page's
            // Retry), not a nameless page asking for vendorIds=undefined.
            if (
                typeof vendor.id !== "number" ||
                !isVendorId(vendor.id) ||
                typeof vendor.name !== "string" ||
                vendor.name === ""
            ) {
                throw new Error(
                    `Unexpected ${endpoint} response: expected a vendor with an id and a name.`,
                );
            }
            return vendor;
        },
        [callApi],
    );

    const createVendor = useCallback(
        async (data: CreateVendorRequest): Promise<CreateVendorResponse> => {
            setIsProcessing(true);
            try {
                const response = await callApi<CreateVendorResponse>(VENDORS_ENDPOINT, {
                    method: "POST",
                    body: JSON.stringify(data),
                });

                const newVendor: VendorResponse = {
                    id: response.id,
                    name: data.name,
                    isActive: true,
                };

                setVendors([...vendors, newVendor]);
                return response;
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, vendors, setVendors],
    );

    const updateVendor = useCallback(
        async (id: number, data: UpdateVendorRequest): Promise<void> => {
            setIsProcessing(true);
            try {
                await callApi<void>(`${VENDORS_ENDPOINT}/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                });
                setVendors(
                    vendors.map((v) => (v.id === id ? { ...v, name: data.name } : v)),
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, vendors, setVendors],
    );

    const getProductsByVendor = useCallback(
        async (
            vendorId: number,
            opts?: { limit?: number; excludeSkuIds?: string[] },
        ): Promise<VendorSku[]> => {
            const params = new URLSearchParams();
            if (opts?.limit != null) {
                params.append("limit", String(opts.limit));
            }
            opts?.excludeSkuIds?.forEach((id) =>
                params.append("excludeSkuIds", id),
            );
            const queryString = params.toString();
            const url = queryString
                ? `${VENDORS_ENDPOINT}/${vendorId}/skus?${queryString}`
                : `${VENDORS_ENDPOINT}/${vendorId}/skus`;
            // Non-critical "Shop {vendor}" rail (#307): a 403 (pre-approval
            // users) or failure here shouldn't toast for a rail the user
            // didn't ask for. The Cart call site catches and ignores the throw.
            return callShipToApi<VendorSku[]>(url, {
                skipErrorNotification: true,
            });
        },
        [callShipToApi],
    );

    const deleteVendor = useCallback(
        async (id: number): Promise<void> => {
            setIsProcessing(true);
            try {
                await callApi<void>(`${VENDORS_ENDPOINT}/${id}`, { method: "DELETE" });
                setVendors(vendors.filter((v) => v.id !== id));
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing, vendors, setVendors],
    );

    return {
        getVendors,
        getVendor,
        getVendorBySlug,
        getProductsByVendor,
        createVendor,
        updateVendor,
        deleteVendor,
    };
};
